import { useState } from "react";
import { Copy, Check, ArrowRight, Mail, Phone, MapPin, Globe, Download, FileImage, FileText, Code2, Package } from "lucide-react";
import { BRAND, waLink } from "../brand";
import PageHero from "../components/site/PageHero";
import { usePageSEO, breadcrumb } from "../hooks/usePageSEO";

// =============================================================
// BRAND MANUAL — page at /marca
// Fully self-contained. Visual design, not functional — shows
// tokens and ready-to-use mockups for print/social formats.
// =============================================================

const COLORS = {
  core: [
    { name: "Negro Daos", hex: "#050505", rgb: "5, 5, 5", role: "Fondo principal premium" },
    { name: "Carbón", hex: "#0A0A0A", rgb: "10, 10, 10", role: "Superficie secundaria" },
    { name: "Grafito", hex: "#111111", rgb: "17, 17, 17", role: "Cards · glass base" },
    { name: "Gris 10", hex: "#1A1A1A", rgb: "26, 26, 26", role: "Bordes · divisores" },
  ],
  gradient: [
    { name: "Rojo Daos", hex: "#FF3B1F", rgb: "255, 59, 31", role: "Inicio del degradado" },
    { name: "Naranja Daos", hex: "#FF8A00", rgb: "255, 138, 0", role: "Acento principal" },
    { name: "Dorado Daos", hex: "#FFD23F", rgb: "255, 210, 63", role: "Highlight premium" },
  ],
  text: [
    { name: "Blanco puro", hex: "#FFFFFF", rgb: "255, 255, 255", role: "Titulares sobre oscuro" },
    { name: "Gris 300", hex: "#D4D4D8", rgb: "212, 212, 216", role: "Texto de apoyo" },
    { name: "Gris 500", hex: "#71717A", rgb: "113, 113, 122", role: "Texto secundario" },
    { name: "Gris 700", hex: "#3F3F46", rgb: "63, 63, 70", role: "Placeholder · notas" },
  ],
  signal: [
    { name: "Verde WhatsApp", hex: "#25D366", rgb: "37, 211, 102", role: "Canal WhatsApp · éxito" },
    { name: "Ámbar Pipeline", hex: "#FFB020", rgb: "255, 176, 32", role: "Estados en espera" },
    { name: "Rojo error", hex: "#F43F5E", rgb: "244, 63, 94", role: "Errores · pérdidas" },
  ],
};

export default function Marca() {
  usePageSEO({
    title: "Manual de Marca · Daos Ads",
    description:
      "Manual de marca oficial de Daos Ads: paleta, tipografía, logotipo y ejemplos listos para cotizaciones, firma de correo, posts, historias y portada de Facebook.",
    path: "/marca",
    jsonLd: [breadcrumb([{ name: "Inicio", path: "/" }, { name: "Marca", path: "/marca" }])],
  });

  return (
    <main>
      <PageHero
        eyebrow="Manual de marca · v1.0"
        title="La identidad visual de"
        highlight="Daos Ads"
        description="Guía completa para mantener la identidad premium de Daos Ads coherente en web, redes sociales, propuestas comerciales y comunicación interna."
        primaryCta={null}
        secondaryCta={null}
      />

      <LogoSection />
      <ColorSection />
      <TypographySection />
      <VoiceSection />

      {/* USAGE EXAMPLES */}
      <ExamplesHeader />
      <QuotationMockup />
      <EmailSignatureMockup />
      <SocialPostMockup />
      <StoryMockup />
      <FacebookCoverMockup />

      <DownloadCTA />
    </main>
  );
}

// =============================================================
// LOGO
// =============================================================
function LogoSection() {
  return (
    <section className="py-16 sm:py-20" data-testid="marca-logo-section">
      <div className="container-x">
        <SectionHeader eyebrow="01 · Logotipo" title="Marca principal e isotipo." />
        <div className="grid lg:grid-cols-12 gap-4 mt-10">
          <div className="lg:col-span-7 grad-border p-8 flex items-center justify-center min-h-[320px] relative overflow-hidden">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#ff3b1f]/20 via-[#ff8a00]/15 to-transparent rounded-full blur-3xl" />
            <div className="relative flex items-center gap-5">
              <img src="/favicon.png" alt="Daos Ads" className="w-24 h-24 sm:w-28 sm:h-28" />
              <div>
                <div className="font-display font-extrabold text-white text-4xl sm:text-5xl tracking-tighter">Daos Ads</div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#ff8a00] font-display font-semibold mt-1">Gana más, pierde cero</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 grad-border p-8 flex items-center justify-center min-h-[320px] bg-white">
            <div className="flex items-center gap-4">
              <img src="/favicon.png" alt="Daos Ads" className="w-20 h-20" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))" }} />
              <div>
                <div className="font-display font-extrabold text-[#050505] text-3xl tracking-tighter">Daos Ads</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#FF8A00] font-display font-semibold mt-1">Gana más, pierde cero</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <RuleCard title="Área de respeto" body="Deja un espacio mínimo equivalente a la altura de la 'D' alrededor del logotipo. No lo encierres en cajas sólidas." />
          <RuleCard title="Tamaño mínimo" body="24 px en digital, 15 mm en impreso. Debajo de eso, usa solo el isotipo." />
          <RuleCard title="Fondos permitidos" body="Preferir negro profundo (#050505). Sobre blanco, usa la versión en negativo. Evita gradientes saturados como fondo." />
          <RuleCard title="Qué evitar" body="No rotar, deformar, pintar con gradientes ajenos, agregar sombras intensas ni rodear de líneas decorativas." />
        </div>
      </div>
    </section>
  );
}

// =============================================================
// COLOR
// =============================================================
function ColorSection() {
  return (
    <section className="py-16 sm:py-20" data-testid="marca-color-section">
      <div className="container-x">
        <SectionHeader eyebrow="02 · Paleta" title="Sistema de color." />

        <h3 className="mt-10 font-display font-semibold text-white text-xl">Superficies oscuras · Core</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {COLORS.core.map((c) => <Swatch key={c.hex} c={c} light={false} />)}
        </div>

        <h3 className="mt-12 font-display font-semibold text-white text-xl">Degradado de marca · Acento principal</h3>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {COLORS.gradient.map((c) => <Swatch key={c.hex} c={c} light />)}
        </div>
        <div className="mt-4 grad-border p-5">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold mb-3">Gradiente Daos</div>
          <div className="h-20 rounded-xl bg-gradient-brand" />
          <code className="mt-3 block text-xs text-zinc-400 break-all">linear-gradient(135deg, #FF3B1F 0%, #FF8A00 50%, #FFD23F 100%)</code>
        </div>

        <h3 className="mt-12 font-display font-semibold text-white text-xl">Texto y tipografía</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {COLORS.text.map((c) => <Swatch key={c.hex} c={c} light={c.hex === "#FFFFFF"} />)}
        </div>

        <h3 className="mt-12 font-display font-semibold text-white text-xl">Colores de señal</h3>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {COLORS.signal.map((c) => <Swatch key={c.hex} c={c} light />)}
        </div>
      </div>
    </section>
  );
}

function Swatch({ c, light }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(c.hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      onClick={copy}
      data-testid={`marca-swatch-${c.hex.replace("#", "")}`}
      className="text-left rounded-2xl overflow-hidden border border-white/10 hover:border-[#ff8a00]/40 transition group"
    >
      <div className="h-28 relative" style={{ background: c.hex }}>
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full glass grid place-items-center opacity-0 group-hover:opacity-100 transition">
          {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-3.5 h-3.5 text-white" />}
        </div>
      </div>
      <div className="p-4 bg-[#0a0a0a]">
        <div className="font-display font-semibold text-white text-sm">{c.name}</div>
        <div className="text-zinc-500 text-xs mt-1">{c.role}</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="text-[11px] text-[#ff8a00] font-display">{c.hex}</code>
          <span className="text-zinc-700 text-[11px]">·</span>
          <code className="text-[11px] text-zinc-400">RGB {c.rgb}</code>
        </div>
      </div>
    </button>
  );
}

// =============================================================
// TYPOGRAPHY
// =============================================================
function TypographySection() {
  return (
    <section className="py-16 sm:py-20" data-testid="marca-typography-section">
      <div className="container-x">
        <SectionHeader eyebrow="03 · Tipografía" title="Sora · Manrope." />

        <div className="grid lg:grid-cols-2 gap-4 mt-10">
          <div className="grad-border p-8">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">Display · titulares</div>
            <div className="mt-4 font-display font-extrabold text-white text-6xl tracking-tighter">Sora</div>
            <div className="mt-2 text-zinc-500 text-sm">Bold · Extrabold · Tracking -0.02em</div>
            <div className="mt-6 space-y-2">
              <div className="font-display font-extrabold text-white text-4xl tracking-tighter">Automatiza. Escala.</div>
              <div className="font-display font-bold text-zinc-200 text-2xl tracking-tight">Convierte más con IA.</div>
              <div className="font-display font-semibold text-zinc-300 text-lg">Servicios y procesos.</div>
            </div>
            <code className="mt-6 block text-xs text-zinc-500 break-all">Google Fonts: Sora — weights 400/600/700/800</code>
          </div>

          <div className="grad-border p-8">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">Body · texto corrido</div>
            <div className="mt-4 font-body font-bold text-white text-6xl">Manrope</div>
            <div className="mt-2 text-zinc-500 text-sm">Regular · Medium · Semibold · Bold</div>
            <div className="mt-6 space-y-3 text-zinc-300 font-body">
              <p className="text-base leading-relaxed">Implemento sistemas de automatización omnicanal que ayudan a empresas a vender más sin perder oportunidades.</p>
              <p className="text-sm text-zinc-400 leading-relaxed">Consultor · operador · implementador. No solo estrategia.</p>
              <p className="text-xs text-zinc-500 uppercase tracking-[0.18em]">Notas legales y fine print</p>
            </div>
            <code className="mt-6 block text-xs text-zinc-500 break-all">Google Fonts: Manrope — weights 300/400/500/600/700/800</code>
          </div>
        </div>

        <div className="mt-4 grad-border p-6">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold mb-4">Escala tipográfica sugerida</div>
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 text-center">
            {[
              ["H1", "60px / 1.04", "Sora 800"],
              ["H2", "40px / 1.05", "Sora 800"],
              ["H3", "24px / 1.3", "Sora 700"],
              ["Lead", "20px / 1.5", "Manrope 500"],
              ["Body", "16px / 1.6", "Manrope 400"],
              ["Caption", "12px · tracking 0.18em", "Sora 600 UPPERCASE"],
            ].map(([label, size, weight]) => (
              <div key={label} className="glass rounded-xl p-4">
                <div className="font-display font-bold text-white text-lg">{label}</div>
                <div className="text-zinc-400 text-xs mt-1">{size}</div>
                <div className="text-[#ff8a00] text-[10px] tracking-[0.14em] uppercase mt-1">{weight}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// VOICE
// =============================================================
function VoiceSection() {
  return (
    <section className="py-16 sm:py-20" data-testid="marca-voice-section">
      <div className="container-x">
        <SectionHeader eyebrow="04 · Voz y tono" title="Operador experto, sin fluff." />
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          <div className="grad-border p-7">
            <div className="text-emerald-400 font-display font-semibold text-sm uppercase tracking-[0.18em]">Sí usamos</div>
            <ul className="mt-4 space-y-2.5 text-zinc-300 text-[15px]">
              <li>— Lenguaje directo y afilado, propio de un operador.</li>
              <li>— Datos concretos: métricas, %, tiempos, ROAS.</li>
              <li>— Verbos de acción: "implemento", "opero", "optimizo".</li>
              <li>— Crítica constructiva al status quo del marketing.</li>
              <li>— Frases cortas. Punto. Otra frase.</li>
            </ul>
          </div>
          <div className="grad-border p-7">
            <div className="text-red-400 font-display font-semibold text-sm uppercase tracking-[0.18em]">Nunca usamos</div>
            <ul className="mt-4 space-y-2.5 text-zinc-300 text-[15px]">
              <li>— Clichés de agencia: "soluciones 360", "sinergia", "engagement".</li>
              <li>— Promesas imposibles sin dato que las sustente.</li>
              <li>— Emojis decorativos excesivos en comunicación comercial.</li>
              <li>— Tonos aspiracionales vacíos sin implementación.</li>
              <li>— Lenguaje infantilizante o paternalista.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// EXAMPLES HEADER
// =============================================================
function ExamplesHeader() {
  return (
    <section className="py-16 sm:py-20" data-testid="marca-examples-header">
      <div className="container-x">
        <SectionHeader eyebrow="05 · Aplicaciones" title="Plantillas listas para usar." />
        <p className="mt-5 text-zinc-400 max-w-2xl text-base">
          Cada plantilla abajo replica exactamente el lenguaje visual web. Puedes capturarlas, usarlas como referencia para Canva/Figma/InDesign, o copiar el HTML y personalizar el contenido.
        </p>
      </div>
    </section>
  );
}

// =============================================================
// QUOTATION MOCKUP
// =============================================================
function QuotationMockup() {
  return (
    <section className="py-10" data-testid="marca-quotation">
      <div className="container-x">
        <SubHeader title="Formato de cotización" hint="A4 · listo para enviar como PDF o PNG" />
        <div className="mt-6 flex flex-wrap gap-2" data-testid="marca-download-cotizacion">
          <DownloadButton href="/brand-assets/cotizacion-daos-A4.pdf" label="Descargar PDF" icon={FileText} testid="dl-cotizacion-pdf" primary />
          <DownloadButton href="/brand-assets/cotizacion-daos-A4.png" label="Descargar PNG" icon={FileImage} testid="dl-cotizacion-png" />
        </div>
        <div className="mt-8 mx-auto max-w-[900px]">
          <div className="grad-border overflow-hidden">
            <div className="bg-[#050505] aspect-[1/1.414] flex flex-col">
              {/* Header */}
              <div className="p-10 flex items-start justify-between relative overflow-hidden">
                <div className="absolute -top-24 -right-20 w-[480px] h-[480px] bg-gradient-to-br from-[#ff3b1f]/25 to-transparent rounded-full blur-3xl" />
                <div className="relative flex items-center gap-3">
                  <img src="/favicon.png" alt="" className="w-12 h-12" />
                  <div>
                    <div className="font-display font-bold text-white text-xl">Daos Ads</div>
                    <div className="text-[9px] tracking-[0.28em] uppercase text-[#ff8a00] font-display">Gana más, pierde cero</div>
                  </div>
                </div>
                <div className="relative text-right">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Propuesta comercial</div>
                  <div className="font-display font-extrabold text-white text-2xl mt-1">#DAOS-2026-001</div>
                  <div className="text-zinc-500 text-xs mt-1">Guatemala · 02 May 2026</div>
                </div>
              </div>

              {/* Client block */}
              <div className="px-10 grid grid-cols-2 gap-8">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00]">Preparada para</div>
                  <div className="text-white font-display font-bold text-lg mt-1">Nombre Cliente S.A.</div>
                  <div className="text-zinc-400 text-sm">Director Comercial</div>
                  <div className="text-zinc-500 text-xs mt-1">contacto@cliente.com</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00]">Preparada por</div>
                  <div className="text-white font-display font-bold text-lg mt-1">David Osorio</div>
                  <div className="text-zinc-400 text-sm">Fundador · Daos Ads</div>
                  <div className="text-zinc-500 text-xs mt-1">david.osorio@daosads.com</div>
                </div>
              </div>

              <div className="mx-10 mt-8 h-px bg-gradient-to-r from-transparent via-[#ff8a00]/40 to-transparent" />

              {/* Scope */}
              <div className="px-10 py-8 flex-1">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00]">Alcance del proyecto</div>
                <h3 className="font-display font-extrabold text-white text-3xl mt-2 tracking-tight leading-tight">
                  Implementación omnicanal con IA +<br />
                  <span className="text-gradient">adquisición paga conectada.</span>
                </h3>

                <div className="mt-6 space-y-2 text-sm text-zinc-300">
                  {[
                    ["01", "Auditoría operativa y plan de trabajo de 30 días."],
                    ["02", "Implementación de Respond.io + IA conversacional."],
                    ["03", "Configuración de campañas Google Ads & Meta Ads."],
                    ["04", "Integración de CRM, tracking y reportes."],
                    ["05", "Entrenamiento del equipo comercial."],
                  ].map(([n, t]) => (
                    <div key={n} className="flex items-start gap-3 glass rounded-lg px-3 py-2.5">
                      <span className="text-[#ff8a00] font-display font-bold text-sm w-6">{n}</span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="mx-10 mb-10 grad-border p-6 flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500">Inversión total</div>
                  <div className="font-display font-extrabold text-white text-4xl mt-1 tracking-tighter">$ 0,000 <span className="text-zinc-500 text-xl font-normal">USD</span></div>
                  <div className="text-zinc-500 text-xs mt-1">Más impuestos aplicables · válida 15 días</div>
                </div>
                <div className="btn-primary !py-3 !px-6 text-sm pointer-events-none">
                  Aceptar propuesta <ArrowRight className="w-4 h-4" />
                </div>
              </div>

              {/* Footer */}
              <div className="px-10 pb-8 flex items-center justify-between text-[10px] text-zinc-600">
                <div>daosads.com · +502 4337 6954</div>
                <div className="tracking-[0.24em] uppercase">Confidencial · uso exclusivo cliente</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// EMAIL SIGNATURE
// =============================================================
function EmailSignatureMockup() {
  return (
    <section className="py-10" data-testid="marca-email-signature">
      <div className="container-x">
        <SubHeader title="Firma de correo" hint="HTML listo para pegar en Gmail / Outlook / Apple Mail" />
        <div className="mt-6 flex flex-wrap gap-2" data-testid="marca-download-firma">
          <DownloadButton href="/brand-assets/firma-correo-daos.html" label="Descargar HTML" icon={Code2} testid="dl-firma-html" primary />
        </div>

        <div className="mt-8 grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 grad-border p-8 bg-white flex items-center min-h-[220px]">
            {/* Clean, email-safe signature (keeps premium hints with a simple accent bar) */}
            <table cellPadding="0" cellSpacing="0" style={{ fontFamily: "Manrope, Arial, sans-serif", color: "#050505", fontSize: 14 }}>
              <tbody>
                <tr>
                  <td style={{ paddingRight: 18, borderRight: "3px solid #FF8A00", verticalAlign: "top" }}>
                    <img src="/favicon.png" alt="Daos Ads" style={{ width: 72, height: 72, display: "block" }} />
                  </td>
                  <td style={{ paddingLeft: 18, verticalAlign: "top" }}>
                    <div style={{ fontFamily: "Sora, Arial, sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em", color: "#050505" }}>
                      David Osorio
                    </div>
                    <div style={{ fontSize: 12, color: "#FF8A00", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 2 }}>
                      Fundador · Daos Ads
                    </div>
                    <div style={{ marginTop: 10, fontSize: 13, color: "#3F3F46", lineHeight: 1.6 }}>
                      <div>✉ david.osorio@daosads.com</div>
                      <div>☎ +502 4337 6954 · WhatsApp</div>
                      <div>🌐 daosads.com</div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11, color: "#71717A", fontStyle: "italic" }}>
                      "Gana más, pierde cero."
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="lg:col-span-2 grad-border p-6">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">Código HTML</div>
            <pre className="mt-3 text-[10.5px] text-zinc-300 overflow-x-auto bg-black/40 rounded-lg p-3 leading-relaxed">{`<table cellspacing="0" cellpadding="0">
 <tr>
  <td style="padding-right:18px;
   border-right:3px solid #FF8A00">
   <img src="LOGO_URL"
    width="72" height="72">
  </td>
  <td style="padding-left:18px;
   font-family:Manrope,Arial">
   <b style="font-family:Sora,Arial;
    font-size:18px">David Osorio</b><br>
   <span style="color:#FF8A00;
    letter-spacing:.18em;
    font-size:12px;
    text-transform:uppercase">
    Fundador · Daos Ads
   </span><br><br>
   david.osorio@daosads.com<br>
   +502 4337 6954<br>
   daosads.com
  </td>
 </tr>
</table>`}</pre>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// SOCIAL POST (Instagram/Facebook feed)
// =============================================================
function SocialPostMockup() {
  return (
    <section className="py-10" data-testid="marca-post">
      <div className="container-x">
        <SubHeader title="Posts de feed · 1080×1350" hint="Formato vertical para Instagram & Facebook · 3 plantillas" />
        <div className="mt-6 flex flex-wrap gap-2" data-testid="marca-download-posts">
          <DownloadButton href="/brand-assets/post-instagram-dato-1080x1350.png" label="Post · El dato" icon={FileImage} testid="dl-post-stat" />
          <DownloadButton href="/brand-assets/post-instagram-quote-1080x1350.png" label="Post · Quote" icon={FileImage} testid="dl-post-quote" />
          <DownloadButton href="/brand-assets/post-instagram-respond-1080x1350.png" label="Post · Servicio" icon={FileImage} testid="dl-post-service" />
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-6">
          {/* POST 1 — Stat */}
          <div className="relative w-[320px] aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 glow-soft bg-[#050505]">
            <div className="absolute inset-0 bg-radial-orange opacity-80" />
            <div className="absolute -top-24 -right-16 w-[380px] h-[380px] bg-gradient-to-br from-[#ff3b1f]/30 to-transparent rounded-full blur-3xl" />
            <div className="relative h-full p-8 flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <img src="/favicon.png" alt="" className="w-8 h-8" />
                <div className="font-display font-bold text-white text-[13px]">Daos Ads</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#ff8a00] font-display font-semibold">El dato</div>
                <div className="font-display font-extrabold text-white text-[44px] leading-none mt-3 tracking-tighter">78%<span className="text-gradient">.</span></div>
                <div className="text-zinc-300 text-sm mt-3 leading-snug">De los clientes compra a <b className="text-white">quien responde primero</b>. ¿Tú en cuánto respondes?</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-display">@daos.ads</div>
                <ArrowRight className="w-4 h-4 text-[#ff8a00]" />
              </div>
            </div>
          </div>

          {/* POST 2 — Quote */}
          <div className="relative w-[320px] aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 glow-soft bg-[#0a0a0a]">
            <div className="absolute inset-0 grain" />
            <div className="relative h-full p-8 flex flex-col">
              <div className="flex items-center gap-2">
                <img src="/favicon.png" alt="" className="w-8 h-8" />
                <div className="font-display font-bold text-white text-[13px]">Daos Ads</div>
              </div>
              <div className="flex-1 flex items-center">
                <div>
                  <div className="text-[44px] text-[#ff8a00] leading-none font-display font-extrabold">"</div>
                  <div className="font-display font-extrabold text-white text-[22px] leading-[1.15] tracking-tight mt-2">
                    No vendas estrategia bonita. <span className="text-gradient">Implementa sistemas</span> que sigan operando sin ti.
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-brand grid place-items-center text-black font-display font-bold text-xs">D</div>
                <div>
                  <div className="text-white font-display font-semibold text-[12px]">David Osorio</div>
                  <div className="text-zinc-500 text-[10px]">Fundador · Daos Ads</div>
                </div>
              </div>
            </div>
          </div>

          {/* POST 3 — Service */}
          <div className="relative w-[320px] aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 glow-soft bg-[#050505]">
            <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-brand" />
            <div className="relative h-full p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-8">
                  <img src="/favicon.png" alt="" className="w-8 h-8" />
                  <div className="font-display font-bold text-white text-[13px]">Daos Ads</div>
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#ff8a00] font-display font-semibold">Servicio</div>
                <div className="font-display font-extrabold text-white text-[30px] leading-[1.05] mt-3 tracking-tighter">
                  Implementación<br /><span className="text-gradient">Respond.io</span>
                </div>
                <div className="text-zinc-400 text-sm mt-4 leading-snug">Centralizo tus canales, implemento IA y dejo a tu equipo operando enterprise-ready.</div>
              </div>
              <div className="glass rounded-xl p-3 text-center">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">Escríbeme</div>
                <div className="text-white font-display font-bold mt-1 text-sm">daosads.com/respond-io</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// STORY
// =============================================================
function StoryMockup() {
  return (
    <section className="py-10" data-testid="marca-story">
      <div className="container-x">
        <SubHeader title="Historias · 1080×1920" hint="Formato vertical para Instagram & Facebook Stories · 3 plantillas" />
        <div className="mt-6 flex flex-wrap gap-2" data-testid="marca-download-stories">
          <DownloadButton href="/brand-assets/story-instagram-tips-1080x1920.png" label="Story · Tips" icon={FileImage} testid="dl-story-tips" />
          <DownloadButton href="/brand-assets/story-instagram-chat-1080x1920.png" label="Story · Chat" icon={FileImage} testid="dl-story-chat" />
          <DownloadButton href="/brand-assets/story-instagram-promo-1080x1920.png" label="Story · Promo" icon={FileImage} testid="dl-story-promo" />
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-6">
          {/* STORY 1 */}
          <div className="relative w-[260px] aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 glow-soft bg-[#050505]">
            <div className="absolute inset-0 bg-radial-orange opacity-90" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-gradient-to-br from-[#ff3b1f]/35 via-[#ff8a00]/20 to-transparent rounded-full blur-3xl" />
            <div className="relative h-full p-7 flex flex-col">
              <div className="flex items-center gap-2">
                <img src="/favicon.png" alt="" className="w-8 h-8" />
                <div className="font-display font-bold text-white text-[12px]">Daos Ads</div>
              </div>
              <div className="flex-1 flex items-center">
                <div>
                  <div className="text-[10px] tracking-[0.26em] uppercase text-[#ff8a00] font-display font-semibold">Nuevo</div>
                  <div className="font-display font-extrabold text-white text-[32px] leading-[1.04] tracking-tighter mt-3">
                    3 errores que te están <span className="text-gradient">costando leads</span>.
                  </div>
                </div>
              </div>
              <div className="glass rounded-xl px-4 py-3 text-center">
                <div className="text-white font-display font-bold text-sm">Desliza hacia arriba</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mt-0.5">daosads.com</div>
              </div>
            </div>
          </div>

          {/* STORY 2 */}
          <div className="relative w-[260px] aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 glow-soft bg-[#0a0a0a]">
            <div className="absolute inset-0 grain" />
            <div className="relative h-full p-7 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="/favicon.png" alt="" className="w-8 h-8" />
                  <div className="font-display font-bold text-white text-[12px]">Daos Ads</div>
                </div>
                <div className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" />
              </div>

              <div className="mt-5 glass-strong rounded-2xl p-3 text-left text-[11px]">
                <div className="text-zinc-200">Hola, ¿manejan automatización con IA?</div>
              </div>
              <div className="mt-2 rounded-2xl p-3 text-left text-[11px] bg-gradient-brand text-black ml-10">
                <div>Sí, te agendo demo mañana 10:30 AM.</div>
              </div>
              <div className="mt-2 glass-strong rounded-2xl p-3 text-left text-[11px]">
                <div className="text-zinc-200">Confirmado.</div>
              </div>

              <div className="mt-auto">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#ff8a00] font-display font-semibold">Así responde tu negocio</div>
                <div className="font-display font-extrabold text-white text-[24px] leading-[1.1] tracking-tighter mt-2">
                  24/7. En segundos.<br/><span className="text-gradient">Sin tocar nada.</span>
                </div>
              </div>
            </div>
          </div>

          {/* STORY 3 — Promo */}
          <div className="relative w-[260px] aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 glow-soft" style={{ background: "linear-gradient(160deg, #050505 0%, #1a0a00 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-2 bg-gradient-brand" />
            <div className="absolute bottom-0 inset-x-0 h-2 bg-gradient-brand" />
            <div className="relative h-full p-8 flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <img src="/favicon.png" alt="" className="w-8 h-8" />
                <div className="font-display font-bold text-white text-[12px]">Daos Ads</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] tracking-[0.26em] uppercase text-[#ff8a00] font-display font-semibold">Consultoría</div>
                <div className="font-display font-extrabold text-white text-[36px] leading-[0.98] mt-4 tracking-tighter">
                  Gana más,<br /><span className="text-gradient">pierde cero.</span>
                </div>
                <div className="mt-4 text-zinc-300 text-xs">Diagnóstico sin compromiso.</div>
              </div>
              <div className="text-center glass rounded-2xl py-3">
                <div className="text-white font-display font-bold text-sm">Agenda ahora</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// FACEBOOK COVER
// =============================================================
function FacebookCoverMockup() {
  return (
    <section className="py-10 pb-20" data-testid="marca-fb-cover">
      <div className="container-x">
        <SubHeader title="Portada de Facebook · 1640×624" hint="Se recorta a 820×312 en mobile · contenido al centro" />
        <div className="mt-6 flex flex-wrap gap-2" data-testid="marca-download-fb-cover">
          <DownloadButton href="/brand-assets/facebook-cover-1640x624.png" label="Descargar portada PNG" icon={FileImage} testid="dl-fb-cover" primary />
        </div>
        <div className="mt-8 mx-auto max-w-[920px]">
          <div className="relative w-full aspect-[820/312] rounded-2xl overflow-hidden border border-white/10 glow-soft bg-[#050505]">
            <div className="absolute inset-0 bg-radial-orange opacity-80" />
            <div className="absolute -top-40 -right-20 w-[700px] h-[700px] bg-gradient-to-br from-[#ff3b1f]/30 via-[#ff8a00]/20 to-transparent rounded-full blur-3xl" />
            <div className="absolute inset-0 grain opacity-50" />

            <div className="relative h-full flex items-center p-8 sm:p-12">
              <div className="flex-1 max-w-[62%]">
                <div className="eyebrow mb-3">Consultor · Operador · Implementador</div>
                <h3 className="font-display font-extrabold text-white text-[clamp(22px,4.5vw,44px)] leading-[1.04] tracking-tighter">
                  Automatiza tu negocio,<br />
                  <span className="text-gradient">escala tus ventas</span> con IA.
                </h3>
                <div className="mt-3 text-zinc-400 text-[clamp(11px,1.3vw,14px)]">
                  Respond.io · Google Ads · Meta Ads · TikTok Ads · Funnels
                </div>
              </div>
              <div className="hidden sm:flex items-center justify-center w-[32%]">
                <img src="/favicon.png" alt="" className="w-28 h-28 sm:w-36 sm:h-36 drop-shadow-[0_8px_40px_rgba(255,138,0,0.4)]" />
              </div>
            </div>

            <div className="absolute bottom-4 left-8 text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-display">
              daosads.com
            </div>
          </div>
        </div>

        <p className="text-zinc-500 text-xs text-center mt-3">Zona segura mobile: mantén el isotipo y headline dentro del recuadro central 820×312.</p>
      </div>
    </section>
  );
}

// =============================================================
// Download CTA
// =============================================================
function DownloadCTA() {
  return (
    <section className="py-20" data-testid="marca-download">
      <div className="container-x">
        <div className="grad-border p-10 sm:p-14 relative overflow-hidden text-center">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#ff3b1f]/20 to-transparent rounded-full blur-3xl" />
          <div className="relative">
            <div className="eyebrow justify-center inline-flex mb-4">Manual de marca · v1.0</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter">
              Todo lo que necesitas en <span className="text-gradient">un solo lugar</span>.
            </h2>
            <p className="mt-4 text-zinc-400 max-w-xl mx-auto text-sm">
              Si un proveedor, diseñador o partner va a producir algo con la marca Daos Ads, compártele esta página:
            </p>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full glass">
              <Globe className="w-4 h-4 text-[#ff8a00]" />
              <code className="text-white font-display font-semibold text-sm">daosads.com/marca</code>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <a href="/brand-assets/daos-ads-brand-kit.zip" download className="btn-primary" data-testid="marca-download-kit">
                <Package className="w-4 h-4" />
                Descargar kit completo (ZIP)
              </a>
              <button onClick={() => window.print()} className="btn-secondary" data-testid="marca-print">
                <Download className="w-4 h-4" />
                Imprimir / Guardar como PDF
              </button>
              <a href={waLink("Hola David, necesito los archivos fuente (logo SVG, tipografías, etc.) del manual de marca de Daos Ads.")} target="_blank" rel="noreferrer" className="btn-secondary">
                <span className="w-2 h-2 rounded-full bg-[#25D366] pulse-dot" />
                Solicitar archivos fuente
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// HELPERS
// =============================================================
function SectionHeader({ eyebrow, title }) {
  return (
    <div className="reveal max-w-2xl">
      <div className="eyebrow mb-4">{eyebrow}</div>
      <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">{title}</h2>
    </div>
  );
}

function SubHeader({ title, hint }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
      <h3 className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight">{title}</h3>
      {hint && <div className="text-zinc-500 text-sm">{hint}</div>}
    </div>
  );
}

function RuleCard({ title, body }) {
  return (
    <div className="grad-border p-5">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display font-semibold">{title}</div>
      <p className="mt-2 text-zinc-300 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function DownloadButton({ href, label, icon: Icon = Download, testid, primary = false }) {
  const cls = primary ? "btn-primary" : "btn-secondary";
  return (
    <a
      href={href}
      download
      target="_blank"
      rel="noreferrer"
      className={`${cls} !py-2.5 !px-4 text-[13px]`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      {label}
    </a>
  );
}
